import { useCallback } from 'react';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useService } from '@atlassian/help-center-common-util/use-service';
import { usePfErrorUtils } from '@atlassian/proforma-common-core/error-utils-context';

import { usePortalId } from '../../controllers/request-create-context-provider';
import { proformaGasAnalyticsFetch } from '../../common/services/proforma-gas-analytics-fetch';
import { JSM_CUSTOMER_PORTAL_URL_PREFIX } from '../constants';
import type { UserPickerUser } from '../users';

/**
 * Field-config-aware user search service for ProForma user picker fields.
 *
 * Uses portalId because this calls a JSM customer portal REST API
 * (/rest/servicedesk/1/customer/portal/{portalId}/...), NOT a ProForma Stargate endpoint.
 *
 * See docs/PORTAL_ID_AND_SERVICE_DESK_ID.md for the distinction.
 */
export const useUsersConfiguredService = () => {
	di(usePortalId);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const errorUtils = usePfErrorUtils();
	const portalId = usePortalId();

	const request = useCallback(
		({
			jiraFieldConfigId,
			jiraFieldKey,
			query,
		}: {
			jiraFieldConfigId: string;
			jiraFieldKey: string;
			query: string;
		}) =>
			proformaGasAnalyticsFetch<UserPickerUser[]>(
				`${JSM_CUSTOMER_PORTAL_URL_PREFIX}/${portalId}/user-search?fieldName=${jiraFieldKey}&fieldConfigId=${jiraFieldConfigId}&query=${encodeURIComponent(
					query,
				)}`,
				{ method: 'GET' },
				createAnalyticsEvent,
				errorUtils,
			),
		[portalId, createAnalyticsEvent, errorUtils],
	);

	return useService(request);
};
